import { TZDate } from '@date-fns/tz'
import { addHours, addMinutes, format } from 'date-fns'

export const monthOnlyFormat = 'LLL yyyy'
export const dateOnlyFormat = 'yyyy-MM-dd'
export const dateTimeFormat = `${dateOnlyFormat} HH:mm:ss z`
export const dateTimeWithoutTimezone = `${dateOnlyFormat} HH:mm:ss`
export const utcISOFormat = `${dateOnlyFormat}'T'HH:mm:ss'Z'`

export const noTerminationTerminationDate = '9999-01-01T05:00:00.000Z'

export const flipDateFn =
  (fn: (d: Date, n: number) => Date, n: number) => (d: Date) =>
    fn(d, n)

export const utc = (date: Date | string | number = new TZDate()): TZDate =>
  new TZDate(date as string, 'UTC')

export const est = (date: Date | string | number = new TZDate()) =>
  new TZDate(date as string, 'EST')

export const stl = (date: Date | string | number = new TZDate()) =>
  new TZDate(date as string, 'America/Chicago')

const NUMS = (count: number) => new RegExp(`(\\d{${count}})`)

const DATE_TIME_SEP = /[T: \\\-/+]+/

const DATE_YYYY_MM_DD = new RegExp(
  [NUMS(4), DATE_TIME_SEP, NUMS(2), DATE_TIME_SEP, NUMS(2)]
    .map((r) => r.source)
    .join(''),
)
const DATE_MM_DD_YYYY = new RegExp(
  [NUMS(2), DATE_TIME_SEP, NUMS(2), DATE_TIME_SEP, NUMS(4)]
    .map((r) => r.source)
    .join(''),
)

const STARTS_WITH_DATE = new RegExp(
  `^((${DATE_YYYY_MM_DD.source})|(${DATE_MM_DD_YYYY.source}))`,
)

const UTC_VARIANTS = /\s*(UTC)|(GMT)|(Z)\s*$/

const OFFSET_WITH_COLON = new RegExp(
  [/\s*/, /([+-])/, NUMS(2), /[:]{1}/, NUMS(2), /\s*/]
    .map((r) => r.source)
    .join(''),
)
const OFFSET_WITHOUT_COLON = new RegExp(
  [/\s*/, /([+-])/, NUMS(2), NUMS(2), /\s*/].map((r) => r.source).join(''),
)

const _getDateComponentsFromDateString = (str: string) => {
  const [c1, c2, c3] = str.split(DATE_TIME_SEP)
  return c1.length === 4
    ? {
      YYYY: (c1 ?? '').replace(/\D/, '').padStart(4, '0'),
      MM: (c2 ?? '').replace(/\D/, '').padStart(2, '0'),
      DD: (c3 ?? '').replace(/\D/, '').padStart(2, '0'),
    }
    : {
      YYYY: (c3 ?? '').replace(/\D/, '').padStart(4, '0'),
      MM: (c1 ?? '').replace(/\D/, '').padStart(2, '0'),
      DD: (c2 ?? '').replace(/\D/, '').padStart(2, '0'),
    }
}

const _getTimeComponentsFromDateString = (str: string) => {
  const [h, m, s] = str
    .replace(STARTS_WITH_DATE, '') // remove date
    .replace(UTC_VARIANTS, '') // remove remove offset
    .replace(OFFSET_WITHOUT_COLON, '') // remove offset
    .replace(OFFSET_WITH_COLON, '') // remove offset
    .replace(/\..*$/, '') // remove millis
    .replace(/T/, '') // remove T
    .trim()
    .split(DATE_TIME_SEP) // split into parts
  return {
    hh: (h ?? '').replace(/\D/, '').padStart(2, '0'),
    mm: (m ?? '').replace(/\D/, '').padStart(2, '0'),
    ss: (s ?? '').replace(/\D/, '').padStart(2, '0'),
  }
}

const _getOffsetFromDateString = (str: string) => {
  const withFormattedOffset = str
    .replace(STARTS_WITH_DATE, '') // remove date
    .replace(UTC_VARIANTS, '+0000') // replace UTC variants
    .replace(OFFSET_WITH_COLON, '$1$2$3') // replace ':' in offset
  return {
    tz: /[+-]{1}\d{4}$/.test(withFormattedOffset)
      ? withFormattedOffset.slice(-5)
      : '-0500',
  } // ensure offset
}

export const sanitizeDateString = (str: string) => {
  // ENSURE STARTS WITH DATE
  if (!STARTS_WITH_DATE.test(str)) {
    // eslint-disable-next-line no-console
    console.error(`Date ${str} could not be parsed into a proper date format`)
    return str
  }

  // CORRECT OFFSET
  const { YYYY, MM, DD } = _getDateComponentsFromDateString(str)
  const { hh, mm, ss } = _getTimeComponentsFromDateString(str)
  const { tz } = _getOffsetFromDateString(str)
  return `${YYYY}-${MM}-${DD}T${hh}:${mm}:${ss}${tz}`
}

export const formatAsUTC = (date: Date, allowTime = true) => {
  try {
    const datetime = format(utc(date), utcISOFormat)
    return allowTime ? datetime : datetime.split('T')[0]
  } catch {
    const datetime = format(date, utcISOFormat)
    return allowTime ? datetime : datetime.split('T')[0]
  }
}

export const tzDate = (val: string, tz: string) => {
  if(!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
    // eslint-disable-next-line no-console
    console.warn(`tzDate was called with ${val} which does not match expected format yyy-MM-ddTHH:mm:ss`)
  }
  const d = new Date(val)
  return addMinutes(addHours(new TZDate(d.getFullYear(), d.getMonth(), d.getDate(), tz), d.getHours()), d.getMinutes())
}

// returns undefined if the argument is a date like the standard non-termination date of '9999-01-01T05:00:00.000Z'
export const getValidIsoDate = (isoDate: string | null | undefined): string | undefined =>
  isoDate && isoDate < '9' ? isoDate : undefined

// for the given ISO date (e.g. 2026-01-22T00:23:00.000Z) returns the current date only in EST/-05:00 (2026-01-21)
// or empty string if a standard termination date (9999-01-01T05:00:00.000Z)
export const formatIsoDateOnly = (fullIsoDate: string | null | undefined): string => {
  const validDate = getValidIsoDate(fullIsoDate)
  if (validDate) {
    return format(new TZDate(validDate, 'EST'), dateOnlyFormat)
  } else {
    return ''
  }
}

export const formatTimestamp = (fullIsoTimestamp: string | number | null | undefined): string => {
  const getTzDate = (): TZDate | undefined => {
    if (fullIsoTimestamp == null) {
      return undefined
    } else if (typeof fullIsoTimestamp === 'number') {
      return new TZDate(new Date(fullIsoTimestamp), 'EST')
    } else {
      return new TZDate(fullIsoTimestamp, 'EST')
    }
  }

  const tzDate = getTzDate()
  return tzDate ? format(tzDate, dateTimeFormat) : ''
}
